import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import EditIcon from '@mui/icons-material/Edit'

import { BoldFormTitle, ErrorText, MedBodyText, SmallBodyText } from '../styles/Text'
import { FlexColumn, FlexEndContainer, PaddedForm, StyledDialog, StyledDialogContent } from '../styles/Containers'
import { MUTED_TEXT } from '../styles/colors'
import Button, { SecondaryButton, SecondaryIconButton } from '../common/Button'
import FilenameInput from './FilenameInput'
import { useRenameFile } from './renameFileRequest'

const FormTitle = styled(BoldFormTitle)`
  text-transform: none;
`

const FieldLabel = styled(MedBodyText)`
  font-weight: 500;
`

const HelperText = styled(SmallBodyText)`
  color: ${MUTED_TEXT};
`

export const RENAME_HELPER_TEXT = 'The extension cannot be changed. The name shown in lists, downloads, and the response package will be updated.'

// Opens the file rename dialog and displays validation errors.
function RenameFileButton({ file, disabled = false, onSuccess = () => {} }) {
  const [isVisible, setIsVisible] = useState(false)
  const rename = useRenameFile(file)
  const canSubmit = rename.canSubmit && !rename.unchanged

  function handleOpen() {
    rename.reset()
    setIsVisible(true)
  }

  function handleClose() {
    if (rename.isSaving) return
    setIsVisible(false)
    rename.reset()
  }

  async function handleSubmit(e) {
    e?.preventDefault?.()
    if (!canSubmit) return
    const updated = await rename.submit()
    if (updated) {
      setIsVisible(false)
      onSuccess(updated)
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      e.preventDefault()
      handleClose()
    }
  }

  return (
    <>
      <SecondaryIconButton aria-label="Rename File" onClick={handleOpen} disabled={disabled}>
        <EditIcon />
      </SecondaryIconButton>
      <StyledDialog fullWidth open={isVisible} onClose={handleClose} keepMounted={false} aria-labelledby="rename-file-title">
        <StyledDialogContent>
          <PaddedForm onSubmit={handleSubmit}>
            <FormTitle id="rename-file-title">Rename file</FormTitle>
            <FlexColumn gap="8px">
              <FieldLabel as="label" htmlFor="rename-file-input">
                File name
              </FieldLabel>
              <FilenameInput
                id="rename-file-input"
                value={rename.stem}
                extension={rename.extension}
                onChange={rename.setStem}
                onKeyDown={handleKeyDown}
                error={!!rename.errorText}
                disabled={rename.isSaving}
                autoFocus
                ariaDescribedBy="rename-file-helper"
              />
              {rename.errorText ? (
                <ErrorText role="alert" id="rename-file-helper">
                  {rename.errorText}
                </ErrorText>
              ) : (
                <HelperText id="rename-file-helper">{RENAME_HELPER_TEXT}</HelperText>
              )}
            </FlexColumn>
            <FlexEndContainer>
              <SecondaryButton type="button" onClick={handleClose} disabled={rename.isSaving}>
                Cancel
              </SecondaryButton>
              <Button type="submit" loading={rename.isSaving} disabled={!canSubmit}>
                Rename
              </Button>
            </FlexEndContainer>
          </PaddedForm>
        </StyledDialogContent>
      </StyledDialog>
    </>
  )
}

RenameFileButton.propTypes = {
  file: PropTypes.shape({
    id: PropTypes.string.isRequired,
    filename: PropTypes.string.isRequired
  }).isRequired,
  disabled: PropTypes.bool,
  onSuccess: PropTypes.func
}

export default RenameFileButton
